
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import SeoHead from '../../components/feature/SeoHead';
import { getLangPath, type Language } from '../../router/langRoutes';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Về The Daeg International Trade - Xuất Nhập Khẩu Châu Á & Đầu Tư Định Cư Latvia | Riga',
    description:
      'The Daeg International Trade - Công ty xuất nhập khẩu hàng hóa Châu Á tại Latvia, sở hữu chuỗi siêu thị Asian Alley tại Riga. 5+ năm kinh nghiệm, đội ngũ chuyên gia đa ngôn ngữ, dịch vụ xuất khẩu và đầu tư định cư Latvia chuyên nghiệp cho người Việt Nam.',
    keywords:
      'The Daeg International Trade, công ty xuất nhập khẩu Latvia, Asian Alley Riga, xuất khẩu châu Á Baltics, đầu tư định cư Latvia, dịch vụ xuất khẩu EU',
  },
  en: {
    title: 'About The Daeg International Trade - Asian Import-Export & Latvia Residency Investment | Riga',
    description:
      'The Daeg International Trade - Asian goods import-export company in Latvia, owner of Asian Alley supermarket chain in Riga. 5+ years experience, multilingual expert team, professional export and Latvia residency investment services.',
    keywords:
      'The Daeg International Trade, Latvia import-export company, Asian Alley Riga, Asian export Baltics, Latvia residency investment, EU export services',
  },
  lv: {
    title: 'Par The Daeg International Trade - Āzijas imports/eksports & Latvijas uzturēšanās | Rīga',
    description:
      'The Daeg International Trade - Āzijas preču imports/eksports uzņēmums Latvijā, Asian Alley veikalu tīkla īpašnieks Rīgā. 5+ gadu pieredze, daudzvalodu ekspertu komanda.',
    keywords:
      'The Daeg International Trade, Latvijas imports eksports, Asian Alley Rīga, Āzijas eksports Baltija',
  },
};

const AboutPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation('about');
  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: seo.title,
    description: seo.description,
    url: `${SITE_URL}${getLangPath('about', lang)}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: `${SITE_URL}${getLangPath('home', lang)}` },
        { '@type': 'ListItem', position: 2, name: 'Về Chúng Tôi', item: `${SITE_URL}${getLangPath('about', lang)}` },
      ],
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'The Daeg International Trade',
    url: SITE_URL,
    foundingDate: '2019',
    description:
      'Công ty xuất nhập khẩu hàng hóa Châu Á tại Latvia, sở hữu chuỗi siêu thị Asian Alley tại Riga.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Krisjana Barona Iela 96/98',
      addressLocality: 'Riga',
      addressCountry: 'LV',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+371-2572-6969',
      email: 'contact@thedaeg.com',
      contactType: 'customer service',
    },
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 50 },
    sameAs: [
      'https://www.facebook.com/thedaeg',
      'https://www.linkedin.com/company/thedaeg',
    ],
  };

  const values = [
    { icon: 'ri-shield-check-line', title: t('values.items.0.title'), description: t('values.items.0.description') },
    { icon: 'ri-user-heart-line', title: t('values.items.1.title'), description: t('values.items.1.description') },
    { icon: 'ri-lightbulb-line', title: t('values.items.2.title'), description: t('values.items.2.description') },
    { icon: 'ri-trophy-line', title: t('values.items.3.title'), description: t('values.items.3.description') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={getLangPath('about', lang)}
        schemaJson={[aboutSchema, orgSchema]}
      />
      <header>
        <Navbar onConsultClick={() => setIsModalOpen(true)} />
      </header>
      <main>
        <h1 className="sr-only">{t('hero.title')}</h1>

        {/* Hero */}
        <section className="relative pt-24 pb-8 md:pt-32 md:pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://public.readdy.ai/ai/img_res/d2b02e61e19304eb83314b6a0fcfc280.jpg"
              alt="About Us"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-6 font-serif">
              {t('hero.title')}
            </h1>
            <p className="text-xs md:text-xl text-gray-300 max-w-3xl">{t('hero.subtitle')}</p>
          </div>
        </section>

        {/* Intro */}
        <section className="py-8 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-lg md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-6 font-serif">
                  {t('intro.title')}
                </h2>
                <p className="text-xs md:text-base text-gray-600 mb-2 md:mb-4 leading-relaxed">{t('intro.description1')}</p>
                <p className="text-xs md:text-base text-gray-600 leading-relaxed">{t('intro.description2')}</p>
              </div>
              <div className="relative order-1 lg:order-2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20interior%20with%20elegant%20design%20and%20warm%20golden%20lighting%20featuring%20sophisticated%20business%20environment%20with%20comfortable%20meeting%20areas%20and%20contemporary%20furniture&width=800&height=600&seq=about002&orientation=landscape"
                  alt="Our Office"
                  className="rounded-xl md:rounded-2xl shadow-xl md:shadow-2xl w-full h-44 md:h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-8 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 md:mb-16">
              <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-4 font-serif">{t('values.title')}</h2>
              <p className="text-xs md:text-base text-gray-600 max-w-2xl mx-auto">{t('values.subtitle')}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-lg md:rounded-xl p-3 md:p-8 shadow-md md:shadow-lg hover:shadow-xl transition-all border border-gray-100 text-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-gold-400 to-gold-600 rounded-full mb-2 md:mb-6 mx-auto">
                    <i className={`${value.icon} text-lg md:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xs md:text-xl font-bold text-gray-900 mb-1 md:mb-3">{value.title}</h3>
                  <p className="text-[10px] md:text-sm text-gray-600 leading-snug">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Asian Alley */}
        <section className="py-8 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-5 md:mb-16">
              <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-4 font-serif">{t('asianAlley.title')}</h2>
              <p className="text-xs md:text-base text-gray-600 max-w-2xl mx-auto">{t('asianAlley.description')}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 md:gap-8">
              {[
                'https://static.readdy.ai/image/bb12603af26d1f2ac82c77bdc32217c8/01e85f8cbfd3d919f89bf6540caea035.jpeg',
                'https://static.readdy.ai/image/bb12603af26d1f2ac82c77bdc32217c8/08a1ca0bb4a203b4706fda24fffefff0.jpeg',
                'https://static.readdy.ai/image/bb12603af26d1f2ac82c77bdc32217c8/b462389e50cb30ef507351c96e26b8b4.jpeg',
                'https://static.readdy.ai/image/bb12603af26d1f2ac82c77bdc32217c8/9938434a0f8d69353134b3a4747722fa.jpeg',
              ].map((src, i) => (
                <div key={i} className="relative rounded-lg md:rounded-2xl overflow-hidden shadow-md md:shadow-xl">
                  <img src={src} alt={`Asian Alley Supermarket ${i + 1}`} className="w-full h-32 md:h-96 object-cover" />
                </div>
              ))}
            </div>
            <div className="mt-5 md:mt-12 bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-8 border border-gray-200">
              <div className="grid grid-cols-3 gap-3 md:gap-8">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="text-center">
                    <div className="w-9 h-9 md:w-16 md:h-16 flex items-center justify-center bg-gold-100 rounded-full mb-1.5 md:mb-4 mx-auto">
                      <i className={`${['ri-store-2-line','ri-line-chart-line','ri-team-line'][i]} text-lg md:text-3xl text-gold-600`}></i>
                    </div>
                    <h3 className="text-xs md:text-xl font-bold text-gray-900 mb-0.5 md:mb-2">{t(`asianAlley.stats.${i}.value`)}</h3>
                    <p className="text-[10px] md:text-sm text-gray-600 leading-tight">{t(`asianAlley.stats.${i}.label`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 md:mb-10">
              <h2 className="text-xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-4 font-serif">{t('cta.title')}</h2>
              <p className="text-xs md:text-base text-gray-300 max-w-2xl mx-auto">{t('cta.description')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-6 md:mb-10">
              <a href="tel:+37125726969" className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white/15 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-gold-600 rounded-full mb-2 md:mb-3">
                  <i className="ri-phone-line text-lg md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xs md:text-lg font-bold mb-0.5 md:mb-1">{t('cta.phone')}</h3>
                <p className="text-[11px] md:text-sm text-gray-300">+371 2572 6969</p>
                <p className="text-[10px] md:text-xs text-gray-400 mt-0.5">Call / Whatsapp / Viber</p>
              </a>
              <a href="mailto:contact@thedaeg.com" className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white/15 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-gold-600 rounded-full mb-2 md:mb-3">
                  <i className="ri-mail-line text-lg md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xs md:text-lg font-bold mb-0.5 md:mb-1">{t('cta.email')}</h3>
                <p className="text-[11px] md:text-sm text-gray-300">contact@thedaeg.com</p>
                <p className="text-[10px] md:text-xs text-gray-400 mt-0.5">{t('cta.emailNote')}</p>
              </a>
              <Link to={getLangPath('contact', lang)} className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white/15 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-gold-600 rounded-full mb-2 md:mb-3">
                  <i className="ri-map-pin-line text-lg md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xs md:text-lg font-bold mb-0.5 md:mb-1">{t('cta.address')}</h3>
                <p className="text-[11px] md:text-sm text-gray-300">Krisjana Barona Iela 96/98</p>
                <p className="text-[10px] md:text-xs text-gray-400 mt-0.5">{t('cta.addressValue')}</p>
              </Link>
            </div>
            <div className="text-center">
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-5 md:px-8 py-2.5 md:py-4 bg-gold-600 text-white text-sm md:text-lg font-semibold rounded-lg hover:bg-gold-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                {t('cta.button')}
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ScrollToTop />
    </div>
  );
};

export default AboutPage;
